// src/pages/sales/ai.tsx
import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { testimonials } from "@/lib/testimonials";
import {
  BookOpen,
  Users,
  Video,
  PhoneCall,
  FileText,
  Lightbulb,
  Wrench,
  Rocket,
  CheckCircle,
  Star,
  TrendingUp,
  XCircle,
  Presentation,
  Info,
  Gift,
  Calendar,
  ArrowRight,
  Sparkles,
  HelpCircle,
  Map,
} from "lucide-react";
import { User } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import CompactROICalculator from "@/components/widgets/CompactROICalculator";
import { useAccessLevel } from "@/hooks/use-access-level";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  image: string;
}

const RealTestimonial = ({ testimonial }: { testimonial: Testimonial }) => {
  return (
    <div className="testimonial-card bg-white dark:bg-gray-950 p-6 rounded-lg shadow-md mb-8 relative z-10 border border-gray-100 dark:border-gray-800">
      <div className="flex items-center mb-4">
        {testimonial.image ? (
          <img
            src={testimonial.image}
            alt={testimonial.author}
            className="w-12 h-12 rounded-full mr-4 object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
            <User className="w-6 h-6 text-gray-400" />
          </div>
        )}
        <div>
          <h4 className="font-semibold text-gray-800 dark:text-white">
            {testimonial.author}
          </h4>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {testimonial.role}
          </p>
        </div>
      </div>
      <p
        className="text-gray-700 dark:text-gray-300 italic"
        dangerouslySetInnerHTML={{ __html: testimonial.quote }}
      ></p>
    </div>
  );
};

export default function MembershipPage() {
  const { isCommunityMember, isLoaded } = useAccessLevel();

  // Show community member content if they're already a member
  if (isLoaded && isCommunityMember) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950">
        {/* Hero Section for Community Members */}
        <div className="relative bg-gradient-to-r from-purple-600 to-blue-700 text-white py-16 md:py-24 overflow-hidden">
          <div className="absolute inset-0 opacity-40 z-0">
            <div className="absolute inset-0 bg-[url('/images/brick-bg.png')] bg-cover bg-center"></div>
          </div>
          <div className="relative container mx-auto px-4 max-w-[1400px] z-10 text-center">
            <Badge
              variant="secondary"
              className="mb-3 bg-white/60 backdrop-blur-sm px-5 py-2 text-base flex items-center mx-auto w-fit"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Community Member
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
              Thank You for Being a Community Member!
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
              You're part of an exclusive group leading the AI revolution in
              digital marketing
            </p>
            <a
              href="https://mikerhodes.circle.so/c/ai-resources"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button
                size="lg"
                className="bg-white text-purple-600 hover:bg-white/90 transition-colors duration-200 shadow-lg px-10 py-6 text-lg"
              >
                Access Your Community Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>

        {/* January 2026 Update Alert */}
        <div className="container mx-auto px-4 max-w-[1400px] py-8">
          <div className="max-w-[60%] mx-auto">
            <Alert className="border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 dark:border-green-700 text-green-800 dark:text-green-300">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">
                  <Gift className="h-5 w-5 !text-green-600 dark:!text-green-400" />
                </div>
                <div className="flex-1 pr-4">
                  <AlertTitle className="font-bold text-lg mb-2">
                    🎉 January 2026 - 8020brain Included!
                  </AlertTitle>
                  <p className="text-base mb-0">
                    As a valued community member, you get{" "}
                    <strong>FREE access</strong> to 8020brain - your AI-powered
                    business brain system. Build automations, get personalized
                    insights, and transform how you run your business.
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <a
                    href="https://mikerhodes.circle.so/c/8020brain/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-green-600 hover:bg-green-700 text-white whitespace-nowrap">
                      Get Started with 8020brain
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </Alert>
          </div>
        </div>

        {/* Member Benefits Grid */}
        <div className="bg-gray-50 dark:bg-gray-900 py-16">
          <div className="container mx-auto px-4 max-w-[1400px]">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
                Your Community Member Benefits
              </h2>
              <p className="text-xl text-muted-foreground">
                Here's everything available to you as an AdsToAI member
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Your Complete Progression Map",
                  description:
                    "See exactly where you are on the journey from manual Google Ads to AI-powered business. Interactive roadmap showing all 6 phases and what you'll master at each stage.",
                  icon: <Map className="h-10 w-10 text-blue-600" />,
                  highlight: true,
                  isNew: true,
                  link: "https://8020brain.com/progressions",
                },
                {
                  title: "8020brain Included",
                  description:
                    "Your AI-powered business brain system - build automations, get insights, and transform how you work",
                  icon: <Gift className="h-10 w-10 text-green-600" />,
                  highlight: true,
                  isNew: true,
                  link: "https://8020brain.com",
                },
                {
                  title: "Monthly Masterclass Webinars",
                  description:
                    "Exclusive 60-minute sessions on the latest AI developments in Google Ads (next one coming soon!)",
                  icon: <Presentation className="h-10 w-10 text-purple-600" />,
                  highlight: true,
                },
                {
                  title: "Private Community Access",
                  description:
                    "Connect with fellow AI-oriented marketers, share insights, and get your questions answered",
                  icon: <Users className="h-10 w-10 text-purple-600" />,
                  highlight: false,
                },
                {
                  title: "1-on-1 Strategy Call",
                  description:
                    "Your included 25-minute call with Mike to discuss your specific challenges",
                  icon: <PhoneCall className="h-10 w-10 text-purple-600" />,
                  highlight: true,
                },
                {
                  title: "Weekly AI in Action Videos",
                  description:
                    "Short, actionable content showing real AI applications in Google Ads",
                  icon: <Video className="h-10 w-10 text-purple-600" />,
                  highlight: false,
                },
                {
                  title: "PMax Asset Charts & Scripts",
                  description:
                    "Specialized tools and code snippets for Performance Max insights",
                  icon: <Wrench className="h-10 w-10 text-purple-600" />,
                  highlight: true,
                },
                {
                  title: "Custom Prompt Library",
                  description:
                    "Growing collection of AI prompts for Google Ads optimization",
                  icon: <FileText className="h-10 w-10 text-purple-600" />,
                  highlight: false,
                },
                {
                  title: "Priority Beta Access",
                  description:
                    "Be first to try new 8020agent features as they're developed",
                  icon: <Star className="h-10 w-10 text-purple-600" />,
                  highlight: true,
                },
                {
                  title: "Quarterly Expert Sessions",
                  description:
                    "Special webinars with industry experts on emerging trends",
                  icon: <Rocket className="h-10 w-10 text-purple-600" />,
                  highlight: false,
                },
              ].map((benefit, index) => {
                const CardWrapper = benefit.link ? 'a' : 'div';
                const cardProps = benefit.link
                  ? { href: benefit.link, className: "block h-full" }
                  : {};

                return (
                  <CardWrapper key={index} {...cardProps}>
                    <Card
                      className={cn(
                        "transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex flex-col relative h-full",
                        benefit.highlight &&
                          "border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20",
                        benefit.link && "cursor-pointer hover:scale-105",
                      )}
                    >
                      {benefit.isNew && (
                        <Badge className="absolute -top-2 -right-2 bg-green-600 text-white">
                          NEW!
                        </Badge>
                      )}
                      <CardHeader className="flex-shrink-0">
                        <div className="mb-4 bg-purple-100 dark:bg-purple-900/30 w-16 h-16 rounded-full flex items-center justify-center">
                          {benefit.icon}
                        </div>
                        <CardTitle className="text-xl text-gray-900 dark:text-white flex items-center gap-2">
                          {benefit.title}
                          {benefit.link && (
                            <ArrowRight className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-grow">
                        <p className="text-gray-600 dark:text-gray-400">
                          {benefit.description}
                        </p>
                      </CardContent>
                    </Card>
                  </CardWrapper>
                );
              })}
            </div>
          </div>
        </div>

        {/* Quick Links Section */}
        <div className="bg-white dark:bg-gray-950 py-16">
          <div className="container mx-auto px-4 max-w-[1400px]">
            <h2 className="text-2xl font-bold mb-8 text-center text-gray-900 dark:text-white">
              Quick Access to Your Resources
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              <a
                href="https://mikerhodes.circle.so/c/ssm2025/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="w-full h-full p-4 flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-700 rounded-lg hover:shadow-lg hover:border-green-300 dark:hover:border-green-600 transition-all duration-300 group-hover:scale-105 relative overflow-hidden">
                  <div className="absolute top-2 right-2">
                    <Badge className="bg-green-600 text-white text-xs font-bold">
                      NEW!
                    </Badge>
                  </div>
                  <div className="bg-green-100 dark:bg-green-800 p-3 rounded-full mb-3 group-hover:bg-green-200 dark:group-hover:bg-green-700 transition-colors">
                    <Gift className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center mb-2">
                    Scripts & Sheets Mastery
                  </span>
                  <Badge className="bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold px-3 py-1 text-center">
                    FREE ACCESS (€249 VALUE)
                  </Badge>
                </div>
              </a>
              <a
                href="https://mikerhodes.circle.so/c/ai-questions/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="w-full h-full p-4 flex flex-col items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 group-hover:scale-105 min-h-[120px]">
                  <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-full mb-3 group-hover:bg-purple-200 dark:group-hover:bg-purple-700 transition-colors">
                    <HelpCircle className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                    Ask a Question
                  </span>
                </div>
              </a>
              <a
                href="https://mikerhodes.circle.so/c/ai-learning/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="w-full h-full p-4 flex flex-col items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 group-hover:scale-105 min-h-[120px]">
                  <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-full mb-3 group-hover:bg-purple-200 dark:group-hover:bg-purple-700 transition-colors">
                    <BookOpen className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                    AI Learning Hub
                  </span>
                </div>
              </a>
              <a
                href="https://mikerhodes.circle.so/c/ai-resources/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                <div className="w-full h-full p-4 flex flex-col items-center justify-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-300 group-hover:scale-105 min-h-[120px]">
                  <div className="bg-purple-100 dark:bg-purple-800 p-3 rounded-full mb-3 group-hover:bg-purple-200 dark:group-hover:bg-purple-700 transition-colors">
                    <FileText className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white text-center">
                    AI Resources
                  </span>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-12">
          <div className="container mx-auto px-4 max-w-[1400px]">
            <div className="text-center">
              <h3 className="text-xl font-bold mb-4">
                From Ads to AI Community
              </h3>
              <p className="text-gray-400 mb-6">
                Thank you for being part of our exclusive community of
                forward-thinking marketers
              </p>
              <p className="text-gray-500 text-sm">
                © {new Date().getFullYear()} From Ads to AI. All rights
                reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Original sales page content for non-members
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* Big Bold Stripey Banner for Existing Members - Rule: Add to existing files rather than creating new ones */}
      <div className="w-full relative z-50">
        <div
          className="absolute inset-0 w-full h-full"
          aria-hidden="true"
          style={{
            backgroundImage:
              "repeating-linear-gradient(135deg, #facc15 0 30px, #fde047 30px 60px)",
            opacity: 0.85,
          }}
        />
        <div
          className="relative py-6 px-2 text-center font-extrabold text-2xl md:text-3xl text-gray-900 dark:text-gray-900 flex items-center justify-center"
          style={{ letterSpacing: "0.01em" }}
        >
          <a
            href="https://mikerhodes.circle.so/c/ai-resources"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors duration-150"
          >
            Existing members log in here.
          </a>
        </div>
      </div>
      {/* Sticky CTA for mobile */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-950 p-4 border-t z-50">
        <a
          href="https://mikerhodes.circle.so/checkout/a2ai"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full"
        >
          <Button className="w-full bg-purple-600 hover:bg-purple-700">
            Join Now (€799/year)
          </Button>
        </a>
      </div>

      {/* 1. Hero Section */}
      <div className="relative bg-gradient-to-r from-purple-600 to-blue-700 text-white py-16 md:py-24 overflow-hidden">
        {/* Added: Subtle background image overlay */}
        <div className="absolute inset-0 opacity-40 z-0">
          <div className="absolute inset-0 bg-[url('/images/brick-bg.png')] bg-cover bg-center"></div>
        </div>
        {/* End Added */}

        <div className="relative container mx-auto px-4 max-w-[1400px] z-10 text-center">
          <Badge
            variant="secondary"
            className="mb-3 bg-white/60 backdrop-blur-sm px-5 py-2 text-base"
          >
            AdsToAI Membership
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 drop-shadow-lg">
            From Ads to AI: Master the Future of Digital Marketing
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-white/90 max-w-3xl mx-auto">
            Join the exclusive community where Google Ads experts become AI
            pioneers
          </p>
          <div className="flex flex-col items-center justify-center gap-4 mb-6 max-w-md mx-auto">
            <a
              href="https://mikerhodes.circle.so/checkout/a2ai"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full"
            >
              <Button
                size="lg"
                className="w-full px-8 py-6 bg-white text-purple-600 hover:bg-white/90 transition-colors duration-200 shadow-lg text-lg"
              >
                Join Now (€799/year)
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* 2. Membership Benefits Section (What You'll Get) */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="text-center max-w-3xl mx-auto mb-8">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              What You'll Get as an AdsToAI Member
            </h2>
            <p className="text-xl text-muted-foreground">
              Everything you need to stay at the forefront of AI in digital
              marketing
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "8020brain — Your Agency Operating System",
                description:
                  "Your AI-powered agency brain. Build automations, get personalized insights, and transform how you run your business with Claude Code integration. (€999 value)",
                icon: <Sparkles className="h-10 w-10 text-green-600" />,
                highlight: true,
                isNew: true,
                link: "https://8020brain.com",
              },
              {
                title: "PMax Script — Single Account",
                description:
                  "Unlock deep insights into your Performance Max campaigns with detailed asset-level reporting and performance visualization. ($199 value)",
                icon: <Wrench className="h-10 w-10 text-purple-600" />,
                highlight: true,
              },
              {
                title: "PMax Script — MCC Version",
                description:
                  "Manage multiple accounts? Get the same powerful PMax insights across all your client accounts from one dashboard. ($399 value)",
                icon: <Wrench className="h-10 w-10 text-purple-600" />,
                highlight: true,
              },
              {
                title: "Negative Keyword Script",
                description:
                  "Automatically identify wasted spend from irrelevant search terms and build smarter negative keyword lists. ($109 value)",
                icon: <FileText className="h-10 w-10 text-purple-600" />,
                highlight: true,
              },
              {
                title: "Scripts & Sheets Mastery Course",
                description:
                  "Complete training on Google Ads Scripts and Google Sheets automation — from beginner to advanced. (€249 value)",
                icon: <BookOpen className="h-10 w-10 text-purple-600" />,
                highlight: true,
              },
              {
                title: "1-on-1 Strategy Call with Mike",
                description:
                  "Schedule your included 25-minute call to discuss your specific business challenges and AI implementation strategies. (€350 value)",
                icon: <PhoneCall className="h-10 w-10 text-purple-600" />,
                highlight: true,
              },
              {
                title: "Monthly Masterclass Webinars",
                description:
                  "Exclusive 60-minute sessions covering the latest AI developments and their practical applications to Google Ads",
                icon: <Presentation className="h-10 w-10 text-purple-600" />,
                highlight: false,
              },
              {
                title: "Private Community Access",
                description:
                  "Join our members-only Circle community where you can ask questions, share insights, and connect with fellow AI-oriented marketers",
                icon: <Users className="h-10 w-10 text-purple-600" />,
                highlight: false,
              },
              {
                title: "Weekly 'AI in Action' Videos",
                description:
                  "Short, actionable content showing real applications of AI in Google Ads management and digital marketing workflows",
                icon: <Video className="h-10 w-10 text-purple-600" />,
                highlight: false,
              },
              {
                title: "Priority Access to Beta Features",
                description:
                  "Be first in line to try new features and tools as they're developed for the 8020agent platform",
                icon: <Star className="h-10 w-10 text-purple-600" />,
                highlight: false,
              },
            ].map((benefit, index) => {
              const CardWrapper = benefit.link ? 'a' : 'div';
              const cardProps = benefit.link
                ? { href: benefit.link, className: "block h-full" }
                : {};

              return (
                <CardWrapper key={index} {...cardProps}>
                  <Card
                    className={cn(
                      "transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1 flex flex-col h-full",
                      benefit.highlight &&
                        "border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20",
                      benefit.link && "cursor-pointer hover:scale-105",
                    )}
                  >
                    {benefit.isNew && (
                      <Badge className="absolute -top-2 -right-2 bg-blue-600 text-white z-10">
                        NEW!
                      </Badge>
                    )}
                    <CardHeader className="flex-shrink-0">
                      <div className="mb-4 bg-purple-100 dark:bg-purple-900/30 w-16 h-16 rounded-full flex items-center justify-center">
                        {benefit.icon}
                      </div>
                      <CardTitle className="text-xl text-gray-900 dark:text-white flex items-center gap-2">
                        {benefit.title}
                        {benefit.link && (
                          <ArrowRight className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <p className="text-gray-600 dark:text-gray-400">
                        {benefit.description}
                      </p>
                    </CardContent>
                    {benefit.highlight && !benefit.isNew && (
                      <div className="absolute top-2 right-2">
                        <Badge
                          variant="secondary"
                          className="bg-purple-200 text-purple-800 dark:bg-purple-800 dark:text-purple-200"
                        >
                          Top Benefit
                        </Badge>
                      </div>
                    )}
                  </Card>
                </CardWrapper>
              );
            })}
          </div>
        </div>
      </div>

      {/* Alert Box */}
      <div className="container mx-auto px-4 max-w-[1400px] pt-12 pb-4">
        <Alert className="border-blue-300 bg-blue-50 dark:border-blue-700 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
          <Info className="h-4 w-4 !text-blue-600 dark:!text-blue-400" />
          <AlertTitle className="font-semibold">
            A Note for AdsToAI Members
          </AlertTitle>
          <AlertDescription>
            This membership program is launching now! While core resources are
            available, our content library is just starting and will grow
            significantly month by month throughout 2025 with new webinars,
            tools, and guides. Your early participation helps shape this
            expanding resource!
          </AlertDescription>
        </Alert>
      </div>

      {/* 3. First Testimonials Row (Indices 0-2) */}
      <div className="bg-white dark:bg-gray-950 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(0, 3).map((testimonial, index) => (
              <RealTestimonial key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>

      {/* 4. Is This Right For You? Section */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Is This The Right Community For You?
          </h2>
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-green-50 dark:bg-green-950/20 p-8 rounded-lg border border-green-100 dark:border-green-900">
              <h3 className="text-2xl font-bold mb-6 text-green-600 dark:text-green-400 flex items-center">
                <CheckCircle className="h-6 w-6 mr-2" /> This is perfect for you
                if:
              </h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                {[
                  "You manage Google Ads accounts and want to understand how AI is changing the landscape.",
                  "You've experimented with AI tools (like ChatGPT) and are ready to integrate them practically into your Google Ads workflow.",
                  "You're looking for actionable AI strategies specifically tailored for Google Ads, not just general concepts.",
                  "You want to connect with and learn alongside fellow forward-thinking Google Ads professionals.",
                  "You understand AI requires ongoing learning and prefer a continuous membership over a single course.",
                  "You aim to enhance your skills and increase your value by mastering AI applications in digital marketing.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />{" "}
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-red-50 dark:bg-red-950/20 p-8 rounded-lg border border-red-100 dark:border-red-900">
              <h3 className="text-2xl font-bold mb-6 text-red-600 dark:text-red-400 flex items-center">
                <XCircle className="h-6 w-6 mr-2" /> This is NOT for you if:
              </h3>
              <ul className="space-y-4 text-gray-700 dark:text-gray-300">
                {[
                  "You're looking for a 'get rich quick' scheme or magic bullet",
                  "You're not willing to implement what you learn",
                  "You expect AI to completely replace human expertise",
                  "You're not interested in Google Ads or digital marketing",
                  "You prefer one-and-done courses rather than ongoing learning",
                  "You're not open to experimenting with new technologies",
                  "You expect AI to be perfect first time",
                  "You don't have curiosity about the future",
                  "You lack imagination",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />{" "}
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Membership Details Section */}
      <div className="bg-white dark:bg-gray-950 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              AdsToAI Member Offer
            </h2>
            <p className="text-xl text-muted-foreground">
              Join the AdsToAI community
            </p>
          </div>
          <div className="max-w-2xl mx-auto">
            <Card className="border-2 border-purple-500 dark:border-purple-400 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
              <CardHeader className="bg-purple-50 dark:bg-purple-950/30 border-b border-purple-100 dark:border-purple-800">
                <CardTitle className="text-center space-y-2">
                  <div className="text-sm text-muted-foreground mb-1">
                    Member Pricing
                  </div>
                  <div className="text-3xl font-bold text-purple-700 dark:text-purple-300">
                    €799{" "}
                    <span className="text-xl font-normal text-muted-foreground">
                      / year
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 text-gray-900 dark:text-white">
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-bold text-lg mb-3 flex items-center">
                      <Star className="h-5 w-5 text-yellow-500 mr-2" />{" "}
                      Included Scripts & Courses
                    </h3>
                    <ul className="space-y-3">
                      {[
                        "PMax Script — single account ($199 value)",
                        "MCC PMax Script — multi-account ($399 value)",
                        "Negative Keyword Script ($109 value)",
                        "Scripts & Sheets Mastery course (€249 value)",
                        "8020brain agency operating system (€999 value)",
                      ].map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />{" "}
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-3 flex items-center">
                      <TrendingUp className="h-5 w-5 text-blue-500 mr-2" />{" "}
                      Community & Support
                    </h3>
                    <ul className="space-y-3">
                      {[
                        "25-minute 1-on-1 strategy call (€350 value)",
                        "Monthly masterclass webinars",
                        "Private community access",
                        "Weekly AI in Action videos",
                        "Priority access to 8020agent features",
                      ].map((item, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />{" "}
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Total Value
                    </div>
                    <div className="font-bold text-xl">
                      Over €1,500 + Ongoing
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">
                      Total Value
                    </div>
                    <div className="font-bold text-xl text-purple-600 dark:text-purple-400">
                      €3,500+
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex-col items-center space-y-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 pt-6">
                <a
                  href="https://mikerhodes.circle.so/checkout/a2ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-1/2"
                >
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 py-5 text-lg">
                    Join Now (€799/year)
                  </Button>
                </a>

                <div className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />{" "}
                    <span>Secure checkout</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <CheckCircle className="h-4 w-4 text-green-500" />{" "}
                    <span>7-day satisfaction guarantee</span>
                  </div>
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>

      {/* 6. Second Testimonials Row (Indices 3-5) */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(3, 6).map((testimonial, index) => (
              <RealTestimonial key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>

      {/* 7. Problem & Solution Section */}
      <div className="bg-white dark:bg-gray-950 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-3xl font-bold mb-4 text-gray-900 dark:text-white">
              Worried about the future of your career?
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">
                The Challenge
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                The digital marketing landscape is changing faster than ever. AI
                isn't coming — it's here. While everyone's talking about AI, few
                understand how to practically apply it to business & Google Ads
                to gain a competitive edge. Staying on the cutting edge without
                wasting time on dead-end technologies feels impossible.
              </p>
              <div className="bg-red-50 dark:bg-red-950/30 p-6 rounded-lg border border-red-100 dark:border-red-900">
                <h4 className="font-bold text-xl mb-3 text-red-600 dark:text-red-400">
                  What's At Stake
                </h4>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  {[
                    "Falling behind competitors who embrace AI-powered marketing",
                    "Wasting hours on tasks that could be automated",
                    "Missing valuable insights hidden in your campaign data",
                    "Spending time learning technologies that won't matter in 6 months",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-red-500 mt-1">•</span>{" "}
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-6">
              <h3 className="text-2xl font-semibold text-purple-600 dark:text-purple-400">
                The Solution
              </h3>
              <p className="text-lg text-gray-700 dark:text-gray-300">
                "From Ads to AI" bridges the gap between your Google Ads
                expertise and the future of AI-powered marketing. Instead of
                scattered information or technical jargon, you'll get clear,
                actionable insights delivered monthly by someone who understands
                both worlds. I break down complex topics and explain them
                simply, so you can implement powerful AI strategies immediately.
              </p>
              <div className="bg-green-50 dark:bg-green-950/30 p-6 rounded-lg border border-green-100 dark:border-green-900">
                <h4 className="font-bold text-xl mb-3 text-green-600 dark:text-green-400">
                  What You'll Gain
                </h4>
                <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                  {[
                    "Monthly guidance on cutting-edge AI applications in Google Ads",
                    "A community of forward-thinking marketers to learn from",
                    "Practical implementations, not just theoretical concepts",
                    "Early access to AI tools and scripts as they're developed",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">•</span>{" "}
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 8. How It Works Section */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-lg border border-purple-100 dark:border-purple-900">
            <h3 className="text-2xl font-bold mb-8 text-center text-gray-900 dark:text-white">
              How It Works - My Goal is to pack this full of value for you!
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4 border border-purple-200 dark:border-purple-700">
                  <span className="text-purple-600 dark:text-purple-300 font-bold text-2xl">
                    1
                  </span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                  Join the FA2A Community
                </h4>
                <p className="text-muted-foreground">
                  Become a member and get immediate access to all resources and
                  the community
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4 border border-purple-200 dark:border-purple-700">
                  <span className="text-purple-600 dark:text-purple-300 font-bold text-2xl">
                    2
                  </span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                  Attend Monthly Webinars
                </h4>
                <p className="text-muted-foreground">
                  Learn the latest AI techniques and applications that apply to
                  Business & Google Ads
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4 border border-purple-200 dark:border-purple-700">
                  <span className="text-purple-600 dark:text-purple-300 font-bold text-2xl">
                    3
                  </span>
                </div>
                <h4 className="font-bold text-lg mb-2 text-gray-900 dark:text-white">
                  Implement & Grow
                </h4>
                <p className="text-muted-foreground">
                  Apply what you learn, share your results, stay ahead of the
                  competition & maybe even get a payrise!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 9. Third Testimonials Row (Indices 6-8) */}
      <div className="bg-white dark:bg-gray-950 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.slice(6, 9).map((testimonial, index) => (
              <RealTestimonial key={index} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </div>

      {/* 10. FAQ Section */}
      <div className="bg-gray-50 dark:bg-gray-900 py-16">
        <div className="container mx-auto px-4 max-w-[900px]">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900 dark:text-white">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                question:
                  "How is this different from the Build The Agent workshop?",
                answer:
                  'While the workshop is an intensive, time-limited training program focused on building AI agents from scratch, "From Ads to AI" is an ongoing membership that keeps you at the forefront of AI developments year-round. You\'ll get regular updates, new strategies, community support, and multiple bonus resources that together create a continuous learning environment focused on practical implementation. BTA attendees get a discounted rate - just reach out to me in the BTA space.',
              },
              {
                question:
                  "Do I need technical skills to benefit from this membership?",
                answer:
                  "Not at all. I specialize in breaking down complex topics into simple, actionable strategies. Whether you're a seasoned developer or completely non-technical, you'll find valuable insights you can implement immediately. Each strategy comes with step-by-step instructions suitable for all skill levels.",
              },
              {
                question: "How much time should I expect to commit each month?",
                answer:
                  "The core commitment is just 60 minutes per month for our live webinar. Everything else—community participation, implementation, watching recordings—is flexible and self-paced to fit your schedule. Workshop attendees have found that spending 2-5 hours per month yields significant results. I think that's a good benchmark for this membership.",
              },
              {
                question: "What if I can't attend the live webinars?",
                answer:
                  "All webinars are recorded and added to the content library, so you'll never miss out. You can also submit questions in advance that will be answered during the session, and the community is available for follow-up discussions on any topic covered.",
              },
              {
                question:
                  "Can I upgrade if I already own the scripts or workshop?",
                answer:
                  "This is different to the scripts & workshops. This is a community membership to deliver you the latest AI strategies, monthly webinars, new tools, and exclusive content. I've made it as affordable as I possibly can (~€40/m) and I expect most members will already own one or more of my products and still find the membership to be incredible value.",
              },
              {
                question: "What happens after my first year of membership?",
                answer:
                  "Your membership renews annually at €799/year. We will remind you before your subscription renews so you can cancel if you need to.",
              },
              {
                question: "Can I cancel my membership if I change my mind?",
                answer:
                  "Yes, you can cancel your membership at any time, and your access will continue until the end of your billing period. ",
              },
              {
                question: "Are there any prerequisites for joining?",
                answer:
                  "The ideal member has some experience with Google Ads and a basic understanding of digital marketing concepts. However, we welcome members at all skill levels who have a desire to learn and implement AI strategies.",
              },
            ].map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="border dark:border-gray-700 rounded-lg px-6 bg-white dark:bg-gray-800"
              >
                <AccordionTrigger className="text-left font-semibold text-lg text-gray-900 dark:text-white hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 dark:text-gray-400 text-base pt-2 pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* 11. About Mike Section (Revised Layout) */}
      <div className="bg-white dark:bg-gray-950 py-16">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            About Your Guide Through the AI Revolution
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 items-start">
            {/* Column 1: Image */}
            <div className="md:col-span-1 flex justify-center md:justify-start">
              <div className="rounded-lg overflow-hidden mb-6 max-w-xs md:max-w-full mx-auto">
                <img
                  src="/images/people/mike.png"
                  alt="Mike Rhodes"
                  className="w-full h-auto object-cover rounded-lg shadow-md"
                />
              </div>
            </div>
            {/* Column 2 & 3: Text */}
            <div className="md:col-span-2">
              <div className="space-y-4 text-lg text-gray-700 dark:text-gray-300">
                <p>
                  For over a decade, I've been at the forefront of Google Ads
                  automation, creating scripts used by thousands of marketers
                  worldwide. I co-wrote the book on Google Ads scripts, my old
                  agency managed well over $100m in ad spend, and I've taught
                  tens of thousands of marketers how to scale their accounts.
                </p>
                <p>
                  Now, I'm focused on the next evolution: building AI agents
                  that transform how we manage digital marketing. I've already
                  guided marketers through this journey, and now I'm opening the
                  doors to a select group of members who want to stay ahead of
                  this revolution.
                </p>
                <p>
                  What makes my approach different? I translate cutting-edge
                  technology into practical applications you can use today. I
                  spend 20+ hours every week building, coding, listening to AI
                  podcasts and experimenting with multiple different AI tools.
                  To find the intersection of Ads, AI & Business. My goal is to
                  help you master this new frontier.
                </p>
                <p>To your continued progress,</p>
                <p>Mike</p>
              </div>
            </div>
          </div>
          {/* Final Testimonials (Below About Mike text) */}
          <div className="mt-12 pt-12 border-t dark:border-gray-700">
            <h3 className="text-2xl font-bold mb-8 text-center text-gray-900 dark:text-white">
              Happy Script & Workshop Buyers
            </h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <RealTestimonial testimonial={testimonials[9]} />
              <RealTestimonial testimonial={testimonials[10]} />
            </div>
          </div>
        </div>
      </div>

      {/* 12. Final Call to Action Section */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-700 text-white py-16">
        <div className="container mx-auto px-4 max-w-[900px] text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Be part of the Future. Today
          </h2>
          <p className="text-xl mb-4 max-w-3xl mx-auto text-white/90">
            The line between those who understand AI and those who don't is
            quickly becoming the line between success and struggle in digital
            marketing. Position yourself on the right side of that line.
          </p>
          <p className="text-lg mb-8 max-w-2xl mx-auto text-white/90">
            Join a select group of Google Ads professionals at the forefront of AI.
          </p>
          <a
            href="https://mikerhodes.circle.so/checkout/a2ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="bg-white text-purple-600 hover:bg-white/0 transition-colors duration-200 shadow-lg px-10 py-6 text-lg"
            >
              Join Now (€799/year)
            </Button>
          </a>
        </div>
      </div>

      {/* AI Time ROI Calculator Widget */}
      <div className="py-16 bg-white dark:bg-gray-950">
        <div className="container mx-auto px-4 max-w-4xl">
          <CompactROICalculator />
        </div>
      </div>

      {/* 13. Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 max-w-[1400px]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">From Ads to AI</h3>
              <p className="text-gray-400">
                Master the future of digital marketing with Mike Rhodes
              </p>
              <div className="mt-4">
                <p className="text-gray-400">
                  © {new Date().getFullYear()} From Ads to AI. E&OE. All rights
                  reserved.
                </p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/buy"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    All Products
                  </a>
                </li>
                <li>
                  <a
                    href="/build"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Build the Agent
                  </a>
                </li>
                <li>
                  <a
                    href="/mastery"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Scripts & Sheets Mastery
                  </a>
                </li>
                <li>
                  <a
                    href="/pmax"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    PMax Script
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/terms"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Terms and Conditions
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
